import colorPallete, { labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    autocompleteGridContainer?: GridContainerProps;
    autocompleteGridItem?: GridItemProps;
    brandName?: InjectableCss;
    erpNumberWrapper?: InjectableCss;
    labelErpValue?: InjectableCss;
    labelErpText?: InjectableCss;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 88px;
            max-height: 88px;
            flex-basis: 88px;
            padding-right: 16px;
        `,
    },
    image: {
        width: "72px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 88px);
            flex-basis: calc(100% - 88px);
        `,
    },
    titleLink: {
        typographyProps: {
            css: css`
                width: 100%;
                text-align: left;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                font: ${labelFonts.label3SemiBold.fontWeight} ${labelFonts.label3SemiBold.fontSize} / 16px
                    ${labelFonts.label3SemiBold.fontFamily};
                &,
                span {
                    color: ${colorPallete.black90};
                }
            `,
        },
        css: css`
            width: 100%;
            margin: 0 0 8px;
        `,
    },
    labelErpValue: {
        css: css`
            color: ${colorPallete.grey800};
        `,
    },
    labelErpText: {
        css: css`
            &,
            b {
                font: ${labelFonts.label2Regular.fontWeight} ${labelFonts.label2Regular.fontSize} /
                    ${labelFonts.label2Regular.lineHeight} ${labelFonts.label2Regular.fontFamily};
            }
            color: ${colorPallete.grey600DisabledText};
            display: flex;
            column-gap: 2px;
            flex-wrap: wrap;
        `,
    },
    erpNumberWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            column-gap: 0;
            > span:not(:empty) {
                padding-right: 16px;
                &:last-child {
                    padding-right: 0;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            flex-direction: column;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    autocompleteGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            border: 1px solid ${colorPallete.grey300};
            border-radius: 8px;
        `,
    },
    autocompleteGridContainer: {
        css: css`
            margin-bottom: 16px;
            > div {
                row-gap: 8px;
                > [class*="GridItemStyle"] {
                    padding: 8px;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            > div {
                                row-gap: 24px;
                                &[class*="GridOffset"] {
                                    align-items: initial;
                                }
                                > div {
                                    max-width: calc(50% - 12px);
                                    flex-basis: calc(50% - 12px);
                                    min-height: 105px;
                                    &:nth-child(odd) {
                                        margin-right: 12px;
                                    }
                                    &:nth-child(even) {
                                        margin-left: 12px;
                                    }
                                }
                            }
                        `,
                        css`
                            margin-bottom: 24px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    brandName: {
        css: css`
            font: ${labelFonts.label3Regular.fontWeight} ${labelFonts.label3Regular.fontSize} / 16px
                ${labelFonts.label3Regular.fontFamily};
            color: ${colorPallete.grey600DisabledText};
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 1;
            overflow: hidden;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        return (
            <>
                {/* <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteProducts_Header"
                    className="autocomplete-products-header"
                >
                    {translate("Products")}
                </Typography> */}
                <GridContainer
                    gap={0}
                    {...styles.autocompleteGridContainer}
                    className="autocomplete-products-main-container"
                >
                    {products.map(product => (
                        <GridItem
                            key={product.id!}
                            {...styles.autocompleteGridItem}
                            className="autocomplete-products-grid-item"
                        >
                            <GridContainer
                                {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                                onClick={() => {
                                    this.props.goToUrl(product.url);
                                }}
                                className="autocomplete-products-container"
                            >
                                <GridItem {...styles.imageGridItem} className="autocomplete-products-image-grid-item">
                                    <LazyImage
                                        {...styles.image}
                                        src={product.image}
                                        className="autocomplete-products-image"
                                    />
                                </GridItem>
                                <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                                    {product.brandName && (
                                        <Typography {...styles.brandName}>{product.brandName}</Typography>
                                    )}

                                    <Link
                                        {...styles.titleLink}
                                        data-test-selector={`productId_${product.id}`}
                                        className="autocomplete-products-link"
                                    >
                                        {product.displayTitle}
                                    </Link>
                                    <StyledWrapper
                                        data-test-selector="autocompleteProducts_PartNumber"
                                        className="autocomplete-products-erp-number"
                                        {...styles.erpNumberWrapper}
                                    >
                                        <Typography {...styles.labelErpText}>
                                            {product.manufacturerItemNumber && (
                                                <>
                                                    {translate("MFG Part#:")}
                                                    <Typography {...styles.labelErpValue}>
                                                        {product.displayManufacturerItemNumber}
                                                    </Typography>
                                                </>
                                            )}
                                        </Typography>
                                        <Typography {...styles.labelErpText}>
                                           {product.displayErpNumber && (
                                                <>
                                                    {translate("DSG#:")}
                                                    <Typography {...styles.labelErpValue}>
                                                        {product.displayErpNumber}
                                                    </Typography>
                                                </>
                                            )}
                                        </Typography>
                                        <Typography {...styles.labelErpText}>
                                            {product.isNameCustomerOverride && product.displayName && (
                                                <>
                                                    {translate("My Part#:")}
                                                    <Typography {...styles.labelErpValue}>
                                                        {product.displayName}
                                                    </Typography>
                                                </>
                                            )}
                                        </Typography>    
                                    </StyledWrapper>
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    ))}
                </GridContainer>
            </>
        );
    }
}

export default AutocompleteProducts;
